
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import Icon from '@ant-design/icons';
import TextWidgetProcessor, {
	TextWidgetState,
	getDefaultState
} from '@kemu-io/kemu-core/widgets/text/index.js';
import { message } from 'antd';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import classNames from 'classnames';
import GateIcon from '../../gateIcon/gateIcon';
import {
	GetPortsInformationFunction,
	GateUI,
	GateUIProps,
	GateCustomSettingsProps,
} from '../index.ts';
import { PortLocation } from '../../../types/canvas_t';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import useTranslation, { getTranslationFunction } from '../../../common/hooks/useTranslation';
import KemuSwitch from '../../form-control/kemuSwitch/KemuSwitch';
import { ABORT_DRAGGING_CLASS, SETTINGS_CONTAINER_CLASS } from '../../../common/constants';
import 'react-resizable/css/styles.css';
import { getLastInputPortWithParent, LastPortInfo } from '../../../common/utils';
import styles from './text.module.css';
import { ReactComponent as TextWidgetIcon } from './icon.svg';

const DEFAULT_W = 100;
const DEFAULT_H = 40;

const TextWidget = (props: GateUIProps): React.JSX.Element => {
	const { repaintExistingPorts, repaintLabels } = props;
	const [isResizing, setIsResizing] = useState(false);
	const textRef = useRef<HTMLDivElement>(null);
	const suppressInputRef = useRef(false);
	const lastProcessedStateRef = useRef<string>('');
	const lastUserTextRef = useRef<string>('');
	const [state, setState] = useReactiveWidgetState<TextWidgetState>(props.recipeId, props.thingRecipeId, props.info.id);
	const [liveSize, setLiveSize] = useState<{ width: number; height: number } | null>(null);
	const fixedState: TextWidgetState = {
		...getDefaultState(),
		...state
	};

	// Calculate the visible text based on the size of the widget
	const textWidth = liveSize?.width || fixedState.size?.width || DEFAULT_W;
	const textHeight = liveSize?.height || fixedState.size?.height || DEFAULT_H;

	const visibleText = useMemo(() => {
		const charWidth = 10;
		const lineHeight = 10;
		const charsPerLine = Math.floor(textWidth / charWidth);
		const lines = Math.floor(textHeight / lineHeight);
		const visibleText = fixedState.text.slice(0, lines * charsPerLine);
		return visibleText;
	}, [textHeight, textWidth, fixedState.text]);

	const handleResizeStart = useCallback(() => {
		setIsResizing(true);
	}, []);

	const handleResizeEnd = useCallback(() => {
		setIsResizing(false);
		setState(current => ({
			...current,
			size: {
				width: liveSize?.width || current.size?.width || DEFAULT_W,
				height: liveSize?.height || current.size?.height || DEFAULT_H
			}
		}));
		// Clear liveSize so the widget reads from recipe state; without this,
		// liveSize outlives the resize and overrides the snapshot when Execute Mode exits.
		setLiveSize(null);
	}, [liveSize, setState]);

	const handleResize = useCallback((event: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
		setLiveSize({ width: data.size.width, height: data.size.height });
		repaintExistingPorts();
		repaintLabels();
	}, [repaintExistingPorts, repaintLabels]);

	const handleTextChange:  React.FormEventHandler<HTMLDivElement> = useCallback((event) => {
		if (suppressInputRef.current) { return; }
		const next = (event.target as HTMLDivElement).innerText;
		lastUserTextRef.current = next;
		setState(current => (current.text === next ? current : { ...current, text: next }));
	}, [setState]);

	/**
	 * Handles paste events to strip formatting and insert plain text only
	 * @param event - The paste event
	 */
	const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		const text = event.clipboardData.getData('text/plain');
		const selection = window.getSelection();
		if (!selection || !textRef.current) { return; }

		if (selection.rangeCount > 0) {
			const range = selection.getRangeAt(0);
			range.deleteContents();
			const textNode = document.createTextNode(text);
			range.insertNode(textNode);
			// Move cursor to end of inserted text
			range.setStartAfter(textNode);
			range.collapse(true);
			selection.removeAllRanges();
			selection.addRange(range);
		}

		// Update state with the new plain text content
		if (textRef.current) {
			const next = textRef.current.innerText;
			lastUserTextRef.current = next;
			setState(current => (current.text === next ? current : { ...current, text: next }));
		}
	}, [setState]);

	// Sync effect for both normal and resize states
	useEffect(() => {
		// This is to keep the DOM element updated with the state WITHOUT causing a re-render
		const el = textRef.current;
		if (!el) { return; }
		const targetText = isResizing ? visibleText : fixedState.text;
		const domText = el.innerText ?? '';
		const isFocused = document.activeElement === el;
		const stateChanged = lastProcessedStateRef.current !== targetText;

		// Detect external update: state changed AND (not from user input OR not focused)
		const isExternalUpdate = stateChanged && (targetText !== lastUserTextRef.current || !isFocused);

		// Update DOM if text doesn't match AND (not focused OR external update)
		const shouldUpdate = domText !== targetText && (!isFocused || isExternalUpdate);

		// Track state changes
		if (stateChanged) {
			lastProcessedStateRef.current = targetText;
			if (isExternalUpdate) {
				lastUserTextRef.current = targetText;
			}
		}

		if (shouldUpdate) {
			suppressInputRef.current = true;
			const frameId = requestAnimationFrame(() => {
				if (el) {
					el.innerText = targetText;
					requestAnimationFrame(() => { suppressInputRef.current = false; });
				}
			});
			return () => cancelAnimationFrame(frameId);
		}
	}, [fixedState.text, isResizing, visibleText]);

	return (
		<Resizable
			height={liveSize?.height || fixedState.size?.height || DEFAULT_H}
			width={liveSize?.width || fixedState.size?.width || DEFAULT_W}
			minConstraints={[DEFAULT_W, DEFAULT_H]}
			onResize={handleResize}
			onResizeStart={handleResizeStart}
			onResizeStop={handleResizeEnd}
		>
			<div className={`${styles.GateBody} ${ABORT_DRAGGING_CLASS}`}
				style={{
					width: (liveSize?.width || fixedState.size?.width || DEFAULT_W),
					height: (liveSize?.height || fixedState.size?.height || DEFAULT_H)
				}}
			>
				<div
					className={styles.TextArea}
					spellCheck={false}
					style={{
						width: '100%',
						height: '100%',
						overflowX: 'hidden',
						overflowY: 'auto',
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
						outline: 'none',
						padding: '10px'
					}}
					autoCorrect='off'
					autoCapitalize='off'
					onInput={handleTextChange}
					onPaste={handlePaste}
					ref={textRef}
					contentEditable={true}
					suppressContentEditableWarning={true}
				/>
			</div>
		</Resizable>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={TextWidgetIcon} />}/>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<TextWidgetState>(props.recipeId, props.blockId, props.gateInfo.id);
	const fixedState = {
		...getDefaultState(),
		...state
	};

	const t = useTranslation('LogicMaker.Gates.Text.Settings');
	const gT = useTranslation('LogicMaker.Gates.Generic');

	const warnIfInputAttached = (info: LastPortInfo | null): boolean => {
		// Prevent adding ports if trigger is linked (otherwise new ports will obtain the connection from trigger)
		if (info && info?.portName === 'trigger') {
			message.warning({
				content: gT('RemoveWarning', 'Please remove all connections from port "{name}"',
				{ name: info.portName })
			});
			return true;
		}

		return false;
	};

	const getLastConnectedInput = () => {
		return getLastInputPortWithParent(
			fixedState,
			TextWidgetProcessor,
			props.gateInfo.id,
			props.blockId,
			props.recipeId,
			props.recipeType
		);
	};

	const handleSwitchChange = (checked: boolean) => {
		const lastPortWithConnection = getLastConnectedInput();
		if (warnIfInputAttached(lastPortWithConnection)) { return ; }
		setState({ ...fixedState, dispatchOnSet: checked });
		props.redrawPortsAndConnections();
	};



	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}

			<div className={styles.SwitchContainer}>
				<label>{t('Switch', 'Trigger next widget on \'set text\'.')}</label>
				<KemuSwitch
					size="small"
					checked={fixedState.dispatchOnSet}
					onChange={handleSwitchChange}
				/>
			</div>
		</div>
	);
};


const getPortsInformation: GetPortsInformationFunction = (state: WidgetState<TextWidgetState>, widgetInfo, intl) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = TextWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = TextWidgetProcessor.getInputNames(state, portContext);
	const t = getTranslationFunction(intl, 'LogicMaker.Gates.Text.Port');

	const inputPositions: Record<string, PortLocation> = {
		'trigger': [0, 0.7, -1, 0],
		'setText': state.dispatchOnSet ? 'Left' : [0, 0.3, -1, 0],
	};

	const outputPositions: Record<string, PortLocation> = {
		'string': [1, 0.3, 1, 0],
		'length': [1, 0.7, 1, 0]
	};

	return {
		inputs: inputNames.map(input => ({
			name: input.name,
			type: input.type,
			position: inputPositions[input.name],
			label: t(input.name, input.name),
		})),

		outputs: outputNames.map(output => ({
			name: output.name,
			type: output.type,
			position: outputPositions[output.name],
			label: output.label
		})),
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: TextWidget,
	CustomSettingsDialog: GateCustomSettings,
	hasTitle: true,
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'LogicMaker.Gates.Text.Title', defaultMessage: 'Text' }),
} as GateUI;
